import { Divider } from '@mui/material';
import axios, { AxiosError } from 'axios';
import { Form, Formik, FormikHelpers }                                                        from 'formik';
import React, { FC, useState } from 'react';
import { Link }                                                                               from 'react-router-dom';
import styled                                                                                 from 'styled-components';
import { LogoBlue }                                                                           from '../../shared/components/Logo';
import { MuiButton }                                                                          from '../../shared/components/MuiButton';
import { EAuthRoutes }                                                                        from '../../shared/constants/constants';
import { TextInputField }                                                                     from '../../shared/formFields/TextInputField';
import { FormFieldWrapper }                                                                   from '../CreateFamily/CreateFamily';
import { IErrorResponse }                                                                     from '../shared/interfaces';
import { AuthField, Instructions, PageHeader, PageHeaderText, SignInUpPage, SignUpContainer } from '../shared/styles';
import { forgotPasswordSchema }                                                               from './validation';
import { Notistack } from "../../shared/components/Notistack/Notistack";
import { AuthResponseKeys } from "../../shared/interfaces/auth";

const Message = styled.div`
  color: #0dc695;
  text-align: center;
  margin: 25px 0 20px;
  font-weight: 700;
`;

interface IForgotPasswordForm {
  email: string;
}

export const ForgotPassword: FC = () => {
  const [ emailSent, setEmailSent ] = useState(false);
  const initialValues: IForgotPasswordForm = {
    email: '',
  };

  const handleSubmit = ({ email }: IForgotPasswordForm, formikActions: FormikHelpers<IForgotPasswordForm>) => {
    formikActions.setSubmitting(true);

    axios.post('/api/v1/verifications/password', { email })
      .then(() => {
        formikActions.setSubmitting(false);
        setEmailSent(true);
        Notistack.enqueueSnackbar('We\'ve sent you an email with instructions to reset your password.', 'success');
      })
      .catch((err: AxiosError<IErrorResponse>) => {
        const errorDetails = err?.response?.data?.ErrorDetails;
        //Display a generic error message if no error details are provided.
        let errorMessage = 'Something went wrong. Please try again.';
        if (errorDetails) {
          const { Key } = errorDetails;
          if(Key === AuthResponseKeys.UNAUTHORIZED) {
            errorMessage = 'This email is not registered with us.';
            formikActions.setSubmitting(false);
            formikActions.setFieldError('email', errorMessage);
          }
        }
        Notistack.enqueueSnackbar(errorMessage, 'error');
      });
  };

  return (
    <SignInUpPage>
      <SignUpContainer maxWidth="md">
        <PageHeader
          mb="25px"
          mbMd="20px"
        >
          <LogoBlue margin="0 0 50px" />

          <PageHeaderText centered>Forgot Password</PageHeaderText>
        </PageHeader>

        { emailSent ? (
          <>
            <Message>Email Sent!</Message>

            <Instructions centered>
              Check your email for instructions to continue.
            </Instructions>
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
                  Return to <Link to={EAuthRoutes.SignIn}>Sign in</Link>
            </div>
          </>
        ) : (
          <>
            <Instructions
              centered
              widthSm="380px"
              widthMd="500px"
            >
              Enter your email address below and we&apos;ll email you how to create a new password.
            </Instructions>

            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={forgotPasswordSchema}
              onSubmit={handleSubmit}
            >
              { ({
                handleBlur,
                handleChange,
                handleSubmit,
                isSubmitting,
              }) => (
                <Form onSubmit={handleSubmit}>
                  <FormFieldWrapper centered>
                    <AuthField
                      label="Email"
                      name={'email'}
                      placeholder="you@example.com"
                      inputProps={{ maxLength: 100 }}
                      component={TextInputField}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />

                    <MuiButton
                      fullWidth
                      type="submit"
                      margin="34px 0 0"
                      disabled={isSubmitting}
                    >
                      Email Me
                    </MuiButton>

                    <Divider />

                    Return to <Link to={EAuthRoutes.SignIn}>Sign in</Link>
                  </FormFieldWrapper>
                </Form>
              ) }
            </Formik>
          </>
        ) }
      </SignUpContainer>
    </SignInUpPage>
  );
};
